import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import {
  doesSectionContainPath,
  findMatchingSubSection,
  getFirstFieldFromSection,
  hasFields,
  hasSubSections,
  normalizeAndCleanPath,
} from 'services/schemableFormNavigation/uiConfigPathHelpers';
import { GenericError } from 'support/helpers/errors/errors';

import { WebEDIError } from './types';

/**
 * Remaps errors to the first cell in a section if the field doesn't exist.
 * Returns both remapped errors and any hidden errors (errors for sections that don't exist in the UI config).
 */
export const remapErrorsToFirstCell = (
  errors: Record<string, WebEDIError>,
  uiConfig: Array<ui.Section>,
): { remappedErrors: Record<string, WebEDIError>; hiddenErrors: Array<WebEDIError> } => {
  const updatedErrors: Record<string, WebEDIError> = {};
  const hiddenErrors: Array<WebEDIError> = [];

  Object.entries(errors).forEach(([errorKey, errorDetail]) => {
    const [section, rowIndex, ...fieldPath] = errorKey.split('.');
    const joinedFieldPath = fieldPath.join('.');

    // Look for the matching section
    const matchingSection = uiConfig.find((uiConfigSection) => uiConfigSection.type === section);

    if (matchingSection && !rowIndex) {
      // If there's no rowIndex, it means it's a section level error
      // In this case, we don't need to remap the error
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    if (!matchingSection) {
      updatedErrors[errorKey] = { ...errorDetail, type: 'hidden' };
      hiddenErrors.push({ ...errorDetail, type: 'hidden' });
      return;
    }

    const fieldExists = doesSectionContainPath(matchingSection, joinedFieldPath);

    if (fieldExists) {
      // If field exists, keep original key and error detail
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    let newKey: string;

    if (hasFields(matchingSection)) {
      const firstField = matchingSection.fields[0];
      if (!firstField) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No fields found in section "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }
      newKey = `${section}.${rowIndex}.${firstField.path}`;
    } else if (hasSubSections(matchingSection)) {
      const matchingSubSection = findMatchingSubSection(matchingSection, joinedFieldPath);

      if (!matchingSubSection) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No sub-sections found in section "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }

      const firstField = getFirstFieldFromSection(matchingSubSection);

      if (!firstField) {
        updatedErrors[errorKey] = errorDetail;
        logError(
          new GenericError(`WebEDI: No fields found in sub-section of "${section}". Keeping original error.`, {
            errorDetail,
            errorKey,
          }),
        );
        return;
      }

      const subSectionPath = normalizeAndCleanPath(matchingSubSection.path);
      const subSectionRowIndex = fieldPath[1] || '0';

      newKey = `${section}.${rowIndex}.${subSectionPath}.${subSectionRowIndex}.${firstField.path}`;
    } else {
      updatedErrors[errorKey] = errorDetail;
      return;
    }

    // Update error detail with new id and fieldPath to restore id === fieldPath invariant
    updatedErrors[newKey] = {
      ...errorDetail,
      id: newKey,
      fieldPath: newKey,
    };
    // Old key is automatically dropped since we're not including it in updatedErrors
  });

  return { remappedErrors: updatedErrors, hiddenErrors };
};
